"use client";

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { User, Shield, FileText, Upload, CheckCircle } from 'lucide-react';
import { showError, showSuccess } from '@/utils/toast';

interface ClientKYCFormProps {
  isWightWayUser?: boolean;
  onComplete?: () => void;
}

export const ClientKYCForm = ({ isWightWayUser = false, onComplete }: ClientKYCFormProps) => {
  const { profile } = useAuth();
  const [step, setStep] = useState<'personal' | 'business' | 'documents' | 'review'>('personal');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [personalData, setPersonalData] = useState({
    full_name: '',
    date_of_birth: '',
    address: '',
    phone: '',
    nationality: '',
  });

  const [businessData, setBusinessData] = useState({
    company_name: '',
    company_number: '',
    incorporation_date: '',
    company_address: '',
    company_type: '',
  });

  const [documentData, setDocumentData] = useState<{
    id_type: string;
    id_number: string;
    id_document_url?: string;
  }>({
    id_type: 'passport',
    id_number: '',
  });

  const handlePersonalSubmit = () => {
    setStep('business');
  };

  const handleBusinessSubmit = () => {
    setStep('documents');
  };

  const handleDocumentsSubmit = async () => {
    if (!profile) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('clients')
        .insert({
          ...personalData,
          ...businessData,
          ...documentData,
          user_id: profile.id,
          kyc_status: 'pending',
        });

      if (error) throw error;

      showSuccess('KYC submitted! Awaiting verification.');
      onComplete?.();
    } catch (error) {
      showError('Failed to submit KYC');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile) {
      showError("Please sign in to upload documents.");
      return;
    }
    const file = e.target.files?.[0];
    if (file) {
      // Upload to Supabase Storage
      const filePath = `kyc/${profile.id}/${Date.now()}-${file.name}`;
      supabase.storage.from('kyc-documents').upload(filePath, file).then(({ data }) => {
        if (data) {
          setDocumentData(prev => ({ ...prev, id_document_url: supabase.storage.from('kyc-documents').getPublicUrl(filePath).data.publicUrl }));
          showSuccess('Document uploaded');
        }
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex items-center space-x-2">
        <Shield className="h-5 w-5" />
        <CardTitle>KYC Verification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === 'personal' && (
          <div className="space-y-4">
            <h3 className="font-medium">Personal Information</h3>
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input id="full_name" value={personalData.full_name} onChange={(e) => setPersonalData(prev => ({ ...prev, full_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input id="date_of_birth" type="date" value={personalData.date_of_birth} onChange={(e) => setPersonalData(prev => ({ ...prev, date_of_birth: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input id="address" value={personalData.address} onChange={(e) => setPersonalData(prev => ({ ...prev, address: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={personalData.phone} onChange={(e) => setPersonalData(prev => ({ ...prev, phone: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nationality">Nationality</Label>
              <Select value={personalData.nationality} onValueChange={(value) => setPersonalData(prev => ({ ...prev, nationality: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select nationality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="uk">UK</SelectItem>
                  <SelectItem value="us">US</SelectItem>
                  <SelectItem value="eu">EU</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handlePersonalSubmit} className="w-full">Next: Business Details</Button>
          </div>
        )}
        {step === 'business' && (
          <div className="space-y-4">
            <h3 className="font-medium">Business Information</h3>
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input id="company_name" value={businessData.company_name} onChange={(e) => setBusinessData(prev => ({ ...prev, company_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_number">Company Number</Label>
              <Input id="company_number" value={businessData.company_number} onChange={(e) => setBusinessData(prev => ({ ...prev, company_number: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="incorporation_date">Incorporation Date</Label>
              <Input id="incorporation_date" type="date" value={businessData.incorporation_date} onChange={(e) => setBusinessData(prev => ({ ...prev, incorporation_date: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_address">Company Address</Label>
              <Input id="company_address" value={businessData.company_address} onChange={(e) => setBusinessData(prev => ({ ...prev, company_address: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_type">Company Type</Label>
              <Select value={businessData.company_type} onValueChange={(value) => setBusinessData(prev => ({ ...prev, company_type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ltd">Ltd</SelectItem>
                  <SelectItem value="plc">PLC</SelectItem>
                  <SelectItem value="sole_trader">Sole Trader</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleBusinessSubmit} className="w-full">Next: Upload Documents</Button>
          </div>
        )}
        {step === 'documents' && (
          <div className="space-y-4">
            <h3 className="font-medium">Upload Identification</h3>
            <div className="space-y-2">
              <Label htmlFor="id_type">ID Type</Label>
              <Select value={documentData.id_type} onValueChange={(value) => setDocumentData(prev => ({ ...prev, id_type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select ID type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driving_license">Driving License</SelectItem>
                  <SelectItem value="national_id">National ID</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="id_number">ID Number</Label>
              <Input id="id_number" value={documentData.id_number} onChange={(e) => setDocumentData(prev => ({ ...prev, id_number: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Upload Document</Label>
              <Input type="file" onChange={handleDocumentUpload} accept=".pdf,.jpg,.png" />
              {documentData.id_document_url && <Badge variant="outline">Uploaded</Badge>}
            </div>
            <Button onClick={handleDocumentsSubmit} disabled={isSubmitting} className="w-full">
              {isSubmitting ? 'Submitting...' : 'Submit KYC'}
            </Button>
          </div>
        )}
        {step === 'review' && (
          <div className="space-y-4">
            <h3 className="font-medium">Review Your Information</h3>
            <div className="space-y-2">
              <p><strong>Personal:</strong> {personalData.full_name}</p>
              <p><strong>Business:</strong> {businessData.company_name}</p>
              <p><strong>ID Type:</strong> {documentData.id_type}</p>
            </div>
            <Button onClick={handleDocumentsSubmit} className="w-full">
              {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};